import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download, Search, Eye, Trash2, RefreshCw, X, Plus, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, Send, Users, CircleCheck, ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createAccount,
  deleteAccount,
  downloadExportTxt,
  listAccounts,
  type AccountFilters,
  type AccountListRow,
  type AccountStats,
  type AccountStatus,
  type AccountSource,
  type AccountSyncStatus,
  type CreateAccountInput,
} from "../api/accounts";
import { listPhones, type Phone } from "../api/phones";
import { StatusBadge } from "../components/StatusBadge";
import { AccountAvatar } from "../components/AccountAvatar";
import { useRealtimeChannel } from "../hooks/useRealtimeChannel";
import { relativeTime } from "../utils/format";

const PAGE_SIZE = 50;
type SortKey = NonNullable<AccountFilters["sort"]>;

function formatCount(n: number | null): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function AccountsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState<AccountListRow[]>([]);
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [total, setTotal] = useState(0);
  const [phones, setPhones] = useState<Phone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<SortKey>("created_at");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [showAdd, setShowAdd] = useState(false);

  const status = (searchParams.get("status") || "") as AccountStatus | "";
  const phoneId = searchParams.get("phone_id") || "";
  const source = (searchParams.get("source") || "") as AccountSource | "";
  const syncStatus = (searchParams.get("sync_status") || "") as AccountSyncStatus | "";
  const q = searchParams.get("q") || "";

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
    setOffset(0);
  }

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await listAccounts({
        status: status || undefined,
        phone_id: phoneId || undefined,
        source: source || undefined,
        sync_status: syncStatus || undefined,
        q: q || undefined,
        sort,
        order,
        limit: PAGE_SIZE,
        offset,
      });
      setAccounts(data.accounts);
      setStats(data.stats);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [status, phoneId, source, syncStatus, q, sort, order, offset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void listPhones().then(setPhones).catch(() => {});
  }, []);

  // Temps réel : un compte créé / synchronisé met la liste à jour.
  const rooms = useMemo(() => ["accounts"], []);
  const onEvent = useCallback(
    (evt: { event: string }) => {
      if (evt.event === "account:created" || evt.event === "account:metrics") {
        void refresh();
      }
    },
    [refresh]
  );
  useRealtimeChannel(rooms, onEvent);

  async function handleDelete(e: React.MouseEvent, acc: AccountListRow) {
    e.stopPropagation();
    if (!window.confirm(t("accounts.confirmDelete"))) return;
    try {
      await deleteAccount(acc.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Suppression échouée");
    }
  }

  async function handleExport() {
    try {
      await downloadExportTxt({ status: status || undefined, phone_id: phoneId || undefined });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Export échoué");
    }
  }

  function toggleSort(key: SortKey) {
    if (sort === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("desc");
    }
    setOffset(0);
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const STATUS_OPTIONS: { value: AccountStatus | ""; label: string }[] = [
    { value: "", label: t("common.all") },
    { value: "active", label: t("status.active") },
    { value: "suspended", label: t("status.suspended") },
    { value: "deleted", label: t("status.deleted") },
    { value: "pending_validation", label: t("status.pending_validation") },
  ];
  const SOURCE_OPTIONS: { value: AccountSource | ""; label: string }[] = [
    { value: "", label: t("common.all") },
    { value: "agent", label: t("accounts.source.agent") },
    { value: "manual", label: t("accounts.source.manual") },
  ];
  const SYNC_OPTIONS: { value: AccountSyncStatus | ""; label: string }[] = [
    { value: "", label: t("common.all") },
    { value: "ok", label: t("accounts.sync.ok") },
    { value: "stale", label: t("accounts.sync.stale") },
    { value: "never", label: t("accounts.sync.never") },
    { value: "error", label: t("accounts.sync.error") },
    { value: "checkpoint", label: t("accounts.sync.checkpoint") },
    { value: "banned", label: t("accounts.sync.banned") },
  ];

  return (
    <div className="p-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t("accounts.title")}</h1>
          <p className="mt-1 text-ink-500 text-sm">
            {t((stats?.total ?? 0) > 1 ? "accounts.countSuffix_plural" : "accounts.countSuffix", { n: stats?.total ?? 0 })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void refresh()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-sm text-ink-500 hover:text-white"
            title={t("common.refresh")}
          >
            <RefreshCw className="size-4" />
          </button>
          <button
            onClick={() => void handleExport()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-white text-sm font-medium"
          >
            <Download className="size-4" />
            {t("accounts.actions.exportTxt")}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium"
          >
            <Plus className="size-4" />
            {t("accounts.actions.add")}
          </button>
        </div>
      </header>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Send} label={t("accounts.stats.postsToday")} value={stats.posts_today} accent="velvet" />
          <StatCard icon={Users} label={t("accounts.stats.created")} value={stats.total} accent="sky" />
          <StatCard icon={CircleCheck} label={t("accounts.stats.active")} value={stats.active} accent="emerald" />
          <StatCard icon={ShieldAlert} label={t("accounts.stats.verification")} value={stats.pending_validation} accent="amber" />
        </div>
      )}

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => updateFilter("q", e.target.value)}
            placeholder={t("accounts.filters.search") + "…"}
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
          />
        </div>
        <FilterSelect value={status} onChange={(v) => updateFilter("status", v)} options={STATUS_OPTIONS} />
        <select
          value={phoneId}
          onChange={(e) => updateFilter("phone_id", e.target.value)}
          className="px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
        >
          <option value="">{t("accounts.filters.device")}</option>
          {phones.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <FilterSelect value={source} onChange={(v) => updateFilter("source", v)} options={SOURCE_OPTIONS} />
        <FilterSelect value={syncStatus} onChange={(v) => updateFilter("sync_status", v)} options={SYNC_OPTIONS} />
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="bg-ink-800 border border-ink-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-700/50 text-ink-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">{t("accounts.columns.username")}</th>
              <th className="text-left px-4 py-3 font-semibold">{t("accounts.columns.status")}</th>
              <SortableTh label={t("accounts.columns.followers")} active={sort === "followers"} order={order} onClick={() => toggleSort("followers")} />
              <SortableTh label={t("accounts.columns.posts")} active={sort === "posts_count"} order={order} onClick={() => toggleSort("posts_count")} />
              <th className="text-left px-4 py-3 font-semibold">{t("accounts.filters.syncStatus")}</th>
              <SortableTh label={t("accounts.columns.lastSync")} active={sort === "last_synced_at"} order={order} onClick={() => toggleSort("last_synced_at")} />
              <th className="text-left px-4 py-3 font-semibold">{t("accounts.columns.device")}</th>
              <th className="text-right px-4 py-3 font-semibold">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {loading && accounts.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">{t("common.loading")}</td></tr>
            )}
            {!loading && accounts.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-ink-500">{t("accounts.empty")}</td></tr>
            )}
            {accounts.map((a) => (
              <tr
                key={a.id}
                onClick={() => navigate(`/accounts/${a.id}`)}
                className="hover:bg-ink-700/30 transition-colors cursor-pointer"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <AccountAvatar accountId={a.id} username={a.ig_username} hasPic={!!a.profile_pic_path} size={36} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">@{a.ig_username}</div>
                      {a.full_name && <div className="text-xs text-ink-500 truncate">{a.full_name}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-3 tabular-nums">{formatCount(a.followers)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCount(a.posts_count)}</td>
                <td className="px-4 py-3"><SyncBadge status={a.sync_status} /></td>
                <td className="px-4 py-3 text-ink-500">{a.last_synced_at ? relativeTime(a.last_synced_at) : "—"}</td>
                <td className="px-4 py-3 text-ink-500">{a.phone_name ?? "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${a.id}`); }}
                      className="px-2 py-1 rounded text-velvet-400 hover:text-velvet-300 hover:bg-velvet-500/10"
                      title={t("accounts.actions.view")}
                    >
                      <Eye className="size-4" />
                    </button>
                    <button
                      onClick={(e) => void handleDelete(e, a)}
                      className="px-2 py-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      title={t("accounts.actions.delete")}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
        <span>{t("accounts.pagination.page", { page, pages })}</span>
        <div className="flex items-center gap-2">
          <button
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-ink-700 hover:border-ink-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="size-4" /> {t("accounts.pagination.prev")}
          </button>
          <button
            disabled={page >= pages}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-ink-700 hover:border-ink-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("accounts.pagination.next")} <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      {showAdd && (
        <AddAccountModal
          phones={phones}
          onClose={() => setShowAdd(false)}
          onCreated={(id) => { setShowAdd(false); navigate(`/accounts/${id}`, { state: { autosync: true } }); }}
        />
      )}
    </div>
  );
}

function FilterSelect<T extends string>({
  value, onChange, options,
}: {
  value: T;
  onChange: (v: string) => void;
  options: { value: T; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SortableTh({
  label, active, order, onClick,
}: {
  label: string;
  active: boolean;
  order: "asc" | "desc";
  onClick: () => void;
}): JSX.Element {
  return (
    <th className="text-left px-4 py-3 font-semibold">
      <button onClick={onClick} className={`inline-flex items-center gap-1 hover:text-white ${active ? "text-white" : ""}`}>
        {label}
        {active && (order === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );
}

export function SyncBadge({ status }: { status: AccountSyncStatus }): JSX.Element {
  const { t } = useTranslation();
  const styles: Record<AccountSyncStatus, string> = {
    never: "bg-ink-700/60 text-ink-500 border-ink-600",
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    stale: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    error: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    checkpoint: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    banned: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status]}`}>
      {t(`accounts.sync.${status}`)}
    </span>
  );
}

type Accent = "velvet" | "sky" | "emerald" | "amber";

function StatCard({
  icon: Icon, label, value, accent,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  accent: Accent;
}): JSX.Element {
  const accents: Record<Accent, { icon: string; glow: string; ring: string }> = {
    velvet: { icon: "bg-velvet-500/15 text-velvet-300", glow: "bg-velvet-500/20", ring: "group-hover:border-velvet-500/40" },
    sky: { icon: "bg-sky-500/15 text-sky-300", glow: "bg-sky-500/20", ring: "group-hover:border-sky-500/40" },
    emerald: { icon: "bg-emerald-500/15 text-emerald-300", glow: "bg-emerald-500/20", ring: "group-hover:border-emerald-500/40" },
    amber: { icon: "bg-amber-500/15 text-amber-300", glow: "bg-amber-500/20", ring: "group-hover:border-amber-500/40" },
  };
  const a = accents[accent];
  return (
    <div className={`group relative overflow-hidden bg-ink-800 border border-ink-700 ${a.ring} rounded-2xl p-5 flex items-center gap-4 transition-colors`}>
      <div className={`absolute -right-8 -top-10 size-28 rounded-full blur-3xl ${a.glow} opacity-60 group-hover:opacity-100 transition-opacity`} />
      <div className={`relative size-12 rounded-xl grid place-items-center ${a.icon}`}>
        <Icon className="size-6" />
      </div>
      <div className="relative">
        <div className="text-3xl font-bold tabular-nums leading-none">{value}</div>
        <div className="mt-1.5 text-xs text-ink-500 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

function AddAccountModal({
  phones, onClose, onCreated,
}: {
  phones: Phone[];
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateAccountInput>({ ig_username: "", two_fa_method: "none" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof CreateAccountInput>(key: K, value: CreateAccountInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    if (!form.ig_username.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const acc = await createAccount({
        ...form,
        ig_username: form.ig_username.trim(),
        phone_id: form.phone_id || undefined,
      });
      onCreated(acc.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("accounts.add.error"));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{t("accounts.add.title")}</h2>
          <button onClick={onClose} className="text-ink-500 hover:text-white"><X className="size-5" /></button>
        </div>

        {err && <div className="mb-3 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">{err}</div>}

        <div className="space-y-3">
          <Field label={t("accounts.add.username")}><Input value={form.ig_username} onChange={(v) => set("ig_username", v)} autoFocus /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("accounts.add.igUserId")}><Input value={form.ig_user_id ?? ""} onChange={(v) => set("ig_user_id", v)} /></Field>
            <Field label={t("accounts.add.fullName")}><Input value={form.full_name ?? ""} onChange={(v) => set("full_name", v)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("accounts.add.email")}><Input value={form.email ?? ""} onChange={(v) => set("email", v)} /></Field>
            <Field label={t("accounts.add.phoneNumber")}><Input value={form.phone_number ?? ""} onChange={(v) => set("phone_number", v)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("accounts.add.password")}><Input value={form.password ?? ""} onChange={(v) => set("password", v)} /></Field>
            <Field label={t("accounts.add.twoFaSecret")}><Input value={form.two_fa_secret ?? ""} onChange={(v) => set("two_fa_secret", v)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("accounts.add.proxy")}><Input value={form.proxy_used ?? ""} onChange={(v) => set("proxy_used", v)} /></Field>
            <Field label={t("accounts.detail.creationIphone")}>
              <select
                value={form.phone_id ?? ""}
                onChange={(e) => set("phone_id", e.target.value || undefined)}
                className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
              >
                <option value="">—</option>
                {phones.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("accounts.add.notes")}>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-sm">{t("accounts.actions.cancel")}</button>
          <button
            onClick={() => void submit()}
            disabled={submitting || !form.ig_username.trim()}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? t("accounts.add.submitting") : t("accounts.add.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs text-ink-500 uppercase tracking-wider font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({
  value, onChange, autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <input
      type="text"
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
    />
  );
}
